import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismDataBar,
  PrismLoadingBar,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import {
  COLOR_TRAFFIC_LIGHT_NEGATIVE,
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  COLOR_TRAFFIC_LIGHT_POSITIVE,
  getCssVariableValue,
} from 'frontend-utils/utils/css-variable';
import HotelOverviewDashboardUpdateIndicator from 'lumen/components/hotel/overview-dashboard/update-indicator';
import formatOtbValue from 'lumen/helpers/format-otb-value';
import getNumberSignClass from 'lumen/helpers/get-number-sign-class';
import isOdTileVisible from 'lumen/helpers/is-od-tile-visible';
import highlightElement from 'lumen/modifiers/highlight-element';
import {
  OD_API_DATA_KEYS,
  OD_SELECTED_TILES_IDS,
} from 'lumen/utils/constants/overview-dashboard-constants';
import { generateRatesTableRedirectUrlQueryParams } from 'lumen/utils/overview-dashboard/generate-rates-redirect-url-query-params';

interface HotelOverviewDashboardTilesTopInsightsOccupancySignature {
  Args: {
    compsetId: unknown;
    data: unknown;
    isLoading: unknown;
    marketOtbUpdateIndicatorContent: unknown;
    otaId: unknown;
    otbUpdateIndicatorContent: unknown;
    rateParam: unknown;
    rentalData: unknown;
    selectedRange: unknown;
    selectedRefDaysAgo: unknown;
    tileId: unknown;
  };
}

export default class HotelOverviewDashboardTilesTopInsightsOccupancy extends Component<HotelOverviewDashboardTilesTopInsightsOccupancySignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odSelections: Services['od-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMarketOtbRefPickupValue() {
    let result = this.intl.t('general.missing');

    if (this.marketOtbRefPickupValue !== null) {
      if (this.marketOtbRefPickupValue === 0) {
        result = this.intl.t('overview_dashboard.kpi.no_change');
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        result = this.intl.formatNumber(this.marketOtbRefPickupValue, {
          signDisplay: 'always',
          style: 'percent',
        });
      }
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOtbRefPickupValue() {
    let result = this.intl.t('general.missing');

    if (this.otbRefPickupValues.otbRefPickupPercentageValue !== null) {
      if (this.otbRefPickupValues.otbRefPickupRoomnightsValue === 0) {
        result = this.intl.t('overview_dashboard.kpi.no_change');
      } else {
        result = this.intl.formatNumber(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.otbRefPickupValues.otbRefPickupPercentageValue,
          {
            signDisplay: 'always',
            style: 'percent',
          },
        );

        const formattedRoomnightsValue = this.intl.formatNumber(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.otbRefPickupValues.otbRefPickupRoomnightsValue,
          { signDisplay: 'always' },
        );

        if (this.otbRefPickupValues.otbRefPickupRoomnightsValue !== null) {
          result += ` (${formattedRoomnightsValue}RN)`;
        }
      }
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRentalOtbRefPickupValue() {
    let result = this.intl.t('general.missing');

    if (this.rentalOtbRefPickupValue !== null) {
      if (this.rentalOtbRefPickupValue === 0) {
        result = this.intl.t('overview_dashboard.kpi.no_change');
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        result = this.intl.formatNumber(this.rentalOtbRefPickupValue, {
          signDisplay: 'always',
          style: 'percent',
        });
      }
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiRedirectUrlQueryParams() {
    let monthToRedirect = '';

    // @ts-expect-error: Incorrect type
    if (this.odSelections.isDayDrilldownActive) {
      monthToRedirect = dayjs(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.odSelections.activeDrilldownDayAsString,
      ).format('YYYY-MM');
    } else {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      monthToRedirect = this.args.selectedRange.start.format('YYYY-MM');
    }

    return generateRatesTableRedirectUrlQueryParams(
      this.hotelAndMarketService,
      monthToRedirect,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.odSelections.refDaysAgoDatesInAPIFormat[
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.args.selectedRefDaysAgo.id
      ],
      // @ts-expect-error: Incorrect type
      this.args.rateParam,
      this.args.otaId,
      this.args.compsetId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketOtbProgressBarData() {
    let data = null;

    // Case 1: marketOtbRefPickupValue (pickup value) can be 0 or null. In that case we just want to visualise the market otb value
    // Case 2: Non zero pickup value will be displayed in the gauge chart in green/red
    if (this.marketOtbValue) {
      const marketOtbValuePercentageNumber = this.marketOtbValue * 100;

      if (this.marketOtbRefPickupValue) {
        // This is the amount of green/red part filled in the gauge chart i.e. pickup
        const marketOtbRefPickupPercentageNumber =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          Math.abs(this.marketOtbRefPickupValue) * 100;
        const marketOtbRefPickupColor =
          this.marketOtbRefPickupValue > 0
            ? getCssVariableValue(COLOR_TRAFFIC_LIGHT_POSITIVE)
            : getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEGATIVE);

        // This is the amount of blue part filled in the gauge chart i.e. otbValue - pickup
        const marketOtbValueAndPickupDiffPercentageNumber =
          marketOtbValuePercentageNumber - marketOtbRefPickupPercentageNumber;

        data = [
          {
            widthPercentage: marketOtbValueAndPickupDiffPercentageNumber,
            backgroundColor: getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL),
          },
          {
            widthPercentage: marketOtbRefPickupPercentageNumber,
            backgroundColor: marketOtbRefPickupColor,
          },
        ];
      } else {
        data = [
          {
            widthPercentage: marketOtbValuePercentageNumber,
            backgroundColor: getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL),
          },
        ];
      }
    }

    return data;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketOtbRefPickupValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.data.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.market_otb_percentage_pickup,
      // @ts-expect-error: Incorrect type
      this.args.selectedRefDaysAgo.id,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketOtbValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.data.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.market_otb_percentage,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbProgressBarData() {
    let data = null;

    // Case 1: otbRefPickupPercentageValue (pickup value) can be 0 or null. In that case we just want to visualise the otb value
    // Case 2: Non zero pickup value will be displayed in the gauge chart in green/red
    if (this.otbValue) {
      const otbValuePercentageNumber = this.otbValue * 100;

      if (this.otbRefPickupValues.otbRefPickupPercentageValue) {
        // This is the amount of green/red part filled in the gauge chart i.e. pickup
        const otbRefPickupPercentageNumber =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          Math.abs(this.otbRefPickupValues.otbRefPickupPercentageValue) * 100;
        const otbRefPickupColor =
          this.otbRefPickupValues.otbRefPickupPercentageValue > 0
            ? getCssVariableValue(COLOR_TRAFFIC_LIGHT_POSITIVE)
            : getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEGATIVE);

        // This is the amount of blue part filled in the gauge chart i.e. otbValue - pickup
        const otbValueAndPickupDiffPercentageNumber =
          otbValuePercentageNumber - otbRefPickupPercentageNumber;

        data = [
          {
            widthPercentage: otbValueAndPickupDiffPercentageNumber,
            backgroundColor: getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL),
          },
          {
            widthPercentage: otbRefPickupPercentageNumber,
            backgroundColor: otbRefPickupColor,
          },
        ];
      } else {
        data = [
          {
            widthPercentage: otbValuePercentageNumber,
            backgroundColor: getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL),
          },
        ];
      }
    }

    return data;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbRefPickupValues() {
    return {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      otbRefPickupPercentageValue: this.args.data.getDataValueFromKey(
        this.odSelections,
        OD_API_DATA_KEYS.my_otb_percentage_pickup,
        // @ts-expect-error: Incorrect type
        this.args.selectedRefDaysAgo.id,
      ),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      otbRefPickupRoomnightsValue: this.args.data.getDataValueFromKey(
        this.odSelections,
        OD_API_DATA_KEYS.my_otb_roomnights_pickup,
        // @ts-expect-error: Incorrect type
        this.args.selectedRefDaysAgo.id,
      ),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.data.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.my_otb_percentage,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalOtbProgressBarData() {
    const rentalOtbRefColor =
      this.rentalOtbRefPickupValue > 0
        ? getCssVariableValue(COLOR_TRAFFIC_LIGHT_POSITIVE)
        : getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEGATIVE);
    return [
      {
        widthPercentage: this.rentalOtbValue * 100,
        backgroundColor: getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL),
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        widthPercentage: Math.abs(this.rentalOtbRefPickupValue) * 100,
        backgroundColor: rentalOtbRefColor,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalOtbRefPickupValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.rentalData?.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.rental_otb_diff,
      // @ts-expect-error: Incorrect type
      this.args.selectedRefDaysAgo.id,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalOtbValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.rentalData?.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.rental_otb,
    );
  }

  <template>
    {{! @glint-nocheck }}
    <div
      class="prism-card--od-kpi prism-card--occupancy 'prism-card--occupancy--without-market-otb'
        {{if
          (not (isOdTileVisible OD_SELECTED_TILES_IDS.market_otb))
          'prism-card--occupancy--without-market-otb'
        }}"
    >
      <PrismCard {{highlightElement id=@tileId isLoading=@isLoading}}>
        <:header as |Header|>
          <Header.title>
            {{t "general.occupancy"}}
          </Header.title>
        </:header>

        <:content>
          <div class="od-kpi-content flex flex-col justify-end">
            {{#if @isLoading}}
              <PrismLoadingBar />
            {{else}}
              <div class="flex flex-row items-start justify-center">
                <div class="occupancy-metric-block flex flex-col">
                  <h3 class="occupancy-metric-block__value">
                    {{formatOtbValue this.otbValue style="percent"}}
                  </h3>
                  <p
                    class="{{getNumberSignClass
                        this.otbRefPickupValues.otbRefPickupPercentageValue
                      }}
                      text-200--regular mb-100"
                  >
                    {{this.formattedOtbRefPickupValue}}
                  </p>
                  <PrismDataBar @data={{this.otbProgressBarData}} />
                  <p
                    class="occupancy-metric-block__label text-200--regular mt-100 flex items-center truncate"
                  >
                    <span class="mr-100">{{t "rates.my_OTB"}}</span>
                    {{#if @otbUpdateIndicatorContent}}
                      <HotelOverviewDashboardUpdateIndicator
                        @updateIndicatorContent={{@otbUpdateIndicatorContent}}
                      />
                    {{/if}}
                  </p>
                </div>

                {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.market_otb)}}
                  <div class="occupancy-metric-block ml-400 flex flex-col">
                    <h3 class="occupancy-metric-block__value">
                      {{formatOtbValue this.marketOtbValue style="percent"}}
                    </h3>
                    <p
                      class="{{getNumberSignClass this.marketOtbRefPickupValue}}
                        text-200--regular mb-100"
                    >
                      {{this.formattedMarketOtbRefPickupValue}}
                    </p>
                    <PrismDataBar @data={{this.marketOtbProgressBarData}} />
                    <p
                      class="occupancy-metric-block__label text-200--regular mt-100 flex items-center truncate"
                    >
                      <span class="mr-100">{{t
                          "rates.tooltip.market_otb"
                        }}</span>
                      {{#if @marketOtbUpdateIndicatorContent}}
                        <HotelOverviewDashboardUpdateIndicator
                          @updateIndicatorContent={{@marketOtbUpdateIndicatorContent}}
                        />
                      {{/if}}
                    </p>
                  </div>
                {{/if}}
                {{#if
                  (isOdTileVisible OD_SELECTED_TILES_IDS.short_term_rental)
                }}
                  <div class="occupancy-metric-block ml-400 flex flex-col">
                    <h3 class="occupancy-metric-block__value">
                      {{formatOtbValue this.rentalOtbValue style="percent"}}
                    </h3>
                    <p
                      class="{{getNumberSignClass this.rentalOtbRefPickupValue}}
                        text-200--regular mb-100"
                    >
                      {{this.formattedRentalOtbRefPickupValue}}
                    </p>
                    <PrismDataBar @data={{this.rentalOtbProgressBarData}} />
                    <p
                      class="occupancy-metric-block__label text-200--regular mt-100 flex items-center truncate"
                    >
                      <span class="mr-100">{{t "short_term_rental.otb"}}</span>
                    </p>
                  </div>
                {{/if}}
              </div>
              <div class="mt-400">
                <LinkTo
                  @model={{this.hotelAndMarketService.hotel.id}}
                  @query={{this.kpiRedirectUrlQueryParams}}
                  @route="hotel.rates"
                  class="prism-link prism-link--standalone"
                  data-event-id="hotel_overview-kpi_main_redirect-click"
                  data-event-value={{@tileId}}
                  target="_blank"
                >
                  {{t "overview_dashboard.kpi.link.rate"}}
                </LinkTo>
              </div>
            {{/if}}
          </div>
        </:content>
      </PrismCard>
    </div>
  </template>
}
